import React, { useState } from "react";
import { CSVLink } from "react-csv";
import DataTable from "react-data-table-component";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";

const ExerciseScheduleList = ({ records = [], loading, onAdd, onEdit, onDelete }) => {
    const [filterText, setFilterText] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const filteredRecords = records.filter((r) =>
        Object.values(r).some((v) =>
            String(v ?? "")
                .toLowerCase()
                .includes(filterText.toLowerCase())
        )
    );

    const totalRows = filteredRecords.length;
    const start = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, totalRows);

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: "Confirm Delete",
            text: "Are you sure you want to delete this exercise schedule?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "Cancel"
        });

        if (result.isConfirmed) {
            try {
                await onDelete(id);
                Swal.fire("Deleted!", "Exercise schedule removed successfully.", "success");
            } catch (error) {
                Swal.fire("Error!", "Failed to delete exercise schedule.", "error");
            }
        }
    };

    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.setFontSize(16);
        doc.text("Exercise Schedules Report", 14, 10);
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 18);

        const columns = [
            "#",
            "Client Name",
            "Responsible Staff",
            "Weight",
            "BMI",
            "Goal",
            "Effective Date"
        ];

        const rows = filteredRecords.map((r, i) => [
            i + 1,
            r.clientName || "-",
            r.responsibleStaff || "-",
            r.weight || "-",
            r.bmi || "-",
            r.goal || "-",
            r.effectiveDate || "-"
        ]);

        autoTable(doc, {
            head: [columns],
            body: rows,
            startY: 25,
            headStyles: { fillColor: [41, 128, 185] },
            alternateRowStyles: { fillColor: [240, 240, 240] }
        });

        doc.save(`exercise_schedules_${new Date().toISOString().split('T')[0]}.pdf`);
    };

    // Prepare CSV data for export
    const csvData = filteredRecords.map((record, index) => ({
        "S.No": index + 1,
        "Client Name": record.clientName || "-",
        "Responsible Staff": record.responsibleStaff || "-",
        "Weight": record.weight || "-",
        "BMI": record.bmi || "-",
        "Goal": record.goal || "-",
        "Plan": record.plan || "-",
        "Effective Date": record.effectiveDate || "-",
        "Case ID": record.caseId || "-",
        "Patient ID": record.patientId || "-"
    }));

    const CustomPagination = ({
        onChangePage,
        onChangeRowsPerPage,
        currentPage,
        rowsPerPage,
        rowCount,
    }) => {
        const totalPages = Math.max(1, Math.ceil(rowCount / rowsPerPage));
        return (
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted small">Rows per page:</span>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: "80px" }}
                        value={rowsPerPage}
                        onChange={(e) => {
                            const newPerPage = Number(e.target.value);
                            onChangeRowsPerPage(newPerPage, currentPage);
                            setRowsPerPage(newPerPage);
                        }}
                    >
                        {[5, 10, 15, 20, 50].map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="d-flex align-items-center gap-1">
                    <button
                        className="btn btn-sm border"
                        onClick={() => {
                            const newPage = Math.max(1, currentPage - 1);
                            onChangePage(newPage, rowCount);
                            setCurrentPage(newPage);
                        }}
                        disabled={currentPage === 1}
                    >
                        <i className="ri-arrow-left-s-line"></i>
                    </button>
                    <span className="mx-3 text-muted small">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        className="btn btn-sm border"
                        onClick={() => {
                            const newPage = Math.min(totalPages, currentPage + 1);
                            onChangePage(newPage, rowCount);
                            setCurrentPage(newPage);
                        }}
                        disabled={currentPage === totalPages}
                    >
                        <i className="ri-arrow-right-s-line"></i>
                    </button>
                </div>
            </div>
        );
    };

    const columns = [
        {
            name: "S.No",
            cell: (row, index) => index + 1 + (currentPage - 1) * rowsPerPage,
            width: "70px",
            sortable: true
        },
        {
            name: "Client Name",
            selector: r => r.clientName || "-",
            sortable: true,
            grow: 2
        },
        {
            name: "Responsible Staff",
            selector: r => r.responsibleStaff || "-",
            sortable: true
        },
        {
            name: "Weight",
            selector: r => r.weight || "-",
            width: "100px"
        },
        {
            name: "Goal",
            selector: r => r.goal || "-",
            width: "150px"
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex gap-2">
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => onEdit(row.id)}
                        title="Edit"
                    >
                        <i className="ri-edit-line"></i>
                    </button>
                    <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(row.id)}
                        title="Delete"
                    >
                        <i className="ri-delete-bin-line"></i>
                    </button>
                </div>
            ),
            width: "130px",
            ignoreRowClick: true
        }
    ];

    return (
        <div>
            <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div className="d-flex align-items-center gap-2">
                    <div className="dropdown">
                        <button
                            className="btn btn-label-primary btn-sm dropdown-toggle"
                            data-bs-toggle="dropdown"
                            disabled={filteredRecords.length === 0}
                        >
                            <i className="ri-download-2-line me-1"></i> Export
                        </button>
                        <ul className="dropdown-menu">
                            <li>
                                <button
                                    className="dropdown-item"
                                    onClick={handleExportPDF}
                                    disabled={filteredRecords.length === 0}
                                >
                                    <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                                </button>
                            </li>
                            <li>
                                <CSVLink
                                    data={csvData}
                                    filename={`exercise_schedules_${new Date().toISOString().split('T')[0]}.csv`}
                                    className="dropdown-item"
                                    onClick={(e) => {
                                        if (filteredRecords.length === 0) {
                                            e.preventDefault();
                                        }
                                    }}
                                >
                                    <i className="ri-file-excel-line me-1 text-success"></i> CSV
                                </CSVLink>
                            </li>
                        </ul>
                    </div>

                    <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Search exercise schedule records..."
                        style={{ width: "260px" }}
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </div>

                <h6 className="mb-0 text-muted flex-grow-1 text-center">
                    {totalRows === 0 ? "No records found" : `Showing ${start}-${end} of ${totalRows} records`}
                </h6>
                <div>
                    <button className="btn btn-sm btn-primary" onClick={onAdd}>
                        <i className="ri-add-line me-1"></i> Add New
                    </button>
                </div>
            </div>
            <div className="card-body p-2">
                <DataTable
                    columns={columns}
                    data={filteredRecords}
                    progressPending={loading}
                    pagination
                    paginationPerPage={rowsPerPage}
                    paginationTotalRows={totalRows}
                    onChangeRowsPerPage={(newPerPage) => {
                        setRowsPerPage(newPerPage);
                    }}
                    onChangePage={(newPage) => {
                        setCurrentPage(newPage);
                    }}
                    paginationComponent={CustomPagination}
                    highlightOnHover
                    striped
                    responsive
                    noDataComponent={
                        <div className="text-center py-4">
                            {filterText ? "No matching records found" : "No exercise schedules found"}
                        </div>
                    }
                />
            </div>
        </div>
    );
};

export default ExerciseScheduleList;